import { useState } from "react";
import { CalendarCheck, Calendar, User, Phone, Mail, Building2, Pencil, Trash2, Check, X } from "lucide-react";
import axios from "axios";
import { updateEvent, deleteEvent, createEvent } from "@/lib/api";
import { getWeekKey, getMonthKey } from "@/lib/chartUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

export default function VisitorsView({ events, onDataChanged }) {
  const [filter, setFilter] = useState("all");
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});

  const visitors = events
    .filter(e => e.type === "guest")
    .sort((a, b) => (b.ts || "").localeCompare(a.ts || ""));

  const scheduled = visitors.filter(v => v.visitStatus === "scheduled");
  const visited = visitors.filter(v => v.visitStatus === "visited");
  const other = visitors.filter(v => !v.visitStatus);

  const filtered = filter === "all" ? visitors
    : filter === "scheduled" ? scheduled
    : filter === "visited" ? visited
    : other;

  async function toggleStatus(e) {
    const newStatus = e.visitStatus === "visited" ? "scheduled" : "visited";
    try {
      await updateEvent(e.id, { visitStatus: newStatus });
      onDataChanged(true);
    } catch { /* */ }
  }

  async function toggleVisitNumber(e) {
    const newNum = (e.visitNumber || 1) === 1 ? 2 : 1;
    const today = new Date().toISOString().slice(0, 10);
    if (newNum === 2) {
      // Update original event with visit number and 2nd visit date
      await updateEvent(e.id, { visitNumber: 2, visit2Date: e.visit2Date || today });
      // Create a new guest event for the current week so stats count it
      const now = new Date();
      await createEvent({
        type: "guest",
        weekKey: getWeekKey(now),
        monthKey: getMonthKey(now),
        ts: now.toISOString(),
        bringer: e.bringer || "Unknown",
        guestName: e.guestName || "Guest",
        guestPhone: e.guestPhone || "",
        guestEmail: e.guestEmail || "",
        guestBiz: e.guestBiz || "",
        visitStatus: "visited",
        visitDate: today,
        visitNumber: 2,
        visit2Date: today,
      });
      onDataChanged(true);
    } else {
      await updateEvent(e.id, { visitNumber: 1 });
      onDataChanged(true);
    }
  }

  function startEdit(v) {
    setEditingId(v.id);
    setEditData({
      guestName: v.guestName || "",
      guestPhone: v.guestPhone || "",
      guestEmail: v.guestEmail || "",
      guestBiz: v.guestBiz || "",
      bringer: v.bringer || "",
    });
  }

  async function saveEdit(id) {
    try {
      await updateEvent(id, editData);
      setEditingId(null);
      onDataChanged(true);
    } catch { /* */ }
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this visitor?")) return;
    try {
      await deleteEvent(id);
      onDataChanged(true);
    } catch { /* */ }
  }

  return (
    <div data-testid="visitors-view">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-base font-bold text-zinc-900">Visitors</h3>
        <span className="text-xs text-zinc-400">{visitors.length} total</span>
      </div>

      {/* Filters */}
      <div className="flex gap-1 flex-wrap mb-4">
        <button onClick={() => setFilter("all")} className={`px-3 py-1 text-[11px] font-bold rounded-md transition-colors ${filter === "all" ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}>
          All ({visitors.length})
        </button>
        <button onClick={() => setFilter("scheduled")} className={`px-3 py-1 text-[11px] font-bold rounded-md transition-colors ${filter === "scheduled" ? "bg-amber-600 text-white" : "bg-amber-50 text-amber-700 hover:bg-amber-100"}`}>
          Scheduled ({scheduled.length})
        </button>
        <button onClick={() => setFilter("visited")} className={`px-3 py-1 text-[11px] font-bold rounded-md transition-colors ${filter === "visited" ? "bg-green-600 text-white" : "bg-green-50 text-green-700 hover:bg-green-100"}`}>
          Visited ({visited.length})
        </button>
        {other.length > 0 && (
          <button onClick={() => setFilter("other")} className={`px-3 py-1 text-[11px] font-bold rounded-md transition-colors ${filter === "other" ? "bg-zinc-600 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}>
            Unset ({other.length})
          </button>
        )}
      </div>

      {/* Visitor List */}
      {filtered.length === 0 ? (
        <div className="text-center py-12">
          <User className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
          <p className="text-sm text-zinc-400">{filter === "all" ? "No visitors logged yet." : `No ${filter} visitors.`}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(v => (
            <div key={v.id} className={`border rounded-xl overflow-hidden ${
              v.visitStatus === "visited" ? "border-green-200 bg-green-50/50" :
              v.visitStatus === "scheduled" ? "border-amber-200 bg-amber-50/50" :
              "border-zinc-200 bg-white"
            }`}>
              <div className="px-4 py-3">
                {editingId === v.id ? (
                  /* Edit Mode */
                  <div className="space-y-2">
                    <input value={editData.guestName} onChange={e => setEditData({...editData, guestName: e.target.value})} placeholder="Guest name" className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-lg outline-none focus:border-orange-500" />
                    <div className="grid grid-cols-2 gap-2">
                      <input value={editData.guestPhone} onChange={e => setEditData({...editData, guestPhone: e.target.value})} placeholder="Phone" className="px-3 py-2 text-sm border border-zinc-200 rounded-lg outline-none focus:border-orange-500" />
                      <input value={editData.guestEmail} onChange={e => setEditData({...editData, guestEmail: e.target.value})} placeholder="Email" className="px-3 py-2 text-sm border border-zinc-200 rounded-lg outline-none focus:border-orange-500" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <input value={editData.guestBiz} onChange={e => setEditData({...editData, guestBiz: e.target.value})} placeholder="Business" className="px-3 py-2 text-sm border border-zinc-200 rounded-lg outline-none focus:border-orange-500" />
                      <input value={editData.bringer} onChange={e => setEditData({...editData, bringer: e.target.value})} placeholder="Invited by" className="px-3 py-2 text-sm border border-zinc-200 rounded-lg outline-none focus:border-orange-500" />
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => saveEdit(v.id)} className="flex-1 inline-flex items-center justify-center gap-1 py-2 text-xs font-bold text-white bg-green-600 hover:bg-green-700 rounded-lg"><Check className="w-3.5 h-3.5" /> Save</button>
                      <button onClick={() => setEditingId(null)} className="flex-1 inline-flex items-center justify-center gap-1 py-2 text-xs font-bold text-zinc-600 bg-zinc-100 hover:bg-zinc-200 rounded-lg"><X className="w-3.5 h-3.5" /> Cancel</button>
                    </div>
                  </div>
                ) : (
                  /* View Mode */
                  <>
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        v.visitStatus === "visited" ? "bg-green-100" :
                        v.visitStatus === "scheduled" ? "bg-amber-100" :
                        "bg-zinc-100"
                      }`}>
                        <User className={`w-5 h-5 ${
                          v.visitStatus === "visited" ? "text-green-600" :
                          v.visitStatus === "scheduled" ? "text-amber-600" :
                          "text-zinc-500"
                        }`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-bold text-zinc-900">{v.guestName || "Unknown Guest"}</p>
                          <button
                            onClick={() => toggleVisitNumber(v)}
                            data-testid={`visit-number-${v.id}`}
                            className={`px-2 py-0.5 text-[10px] font-bold rounded-full transition-colors ${
                              (v.visitNumber || 1) === 2
                                ? "bg-purple-100 text-purple-700 border border-purple-200 hover:bg-purple-200"
                                : "bg-zinc-100 text-zinc-500 border border-zinc-200 hover:bg-zinc-200"
                            }`}
                          >
                            {(v.visitNumber || 1) === 2 ? "2nd visit" : "1st visit"}
                          </button>
                        </div>
                        <div className="flex items-center gap-2 text-[11px] text-zinc-500 mt-0.5 flex-wrap">
                          <span>Invited by <span className="font-semibold text-zinc-700">{v.bringer}</span></span>
                          {v.visitDate && <span>· 1st: {fmtDate(v.visitDate)}</span>}
                          {!v.visitDate && v.ts && <span>· 1st: {fmtDate(v.ts.slice(0, 10))}</span>}
                          {v.visit2Date && <span>· 2nd: {fmtDate(v.visit2Date)}</span>}
                        </div>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <button onClick={() => startEdit(v)} data-testid={`edit-visitor-${v.id}`} className="p-1.5 text-zinc-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors">
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => handleDelete(v.id)} data-testid={`delete-visitor-${v.id}`} className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* Contact info row */}
                    {(v.guestPhone || v.guestEmail || v.guestBiz) && (
                      <div className="flex gap-2 flex-wrap mt-2 ml-13">
                        {v.guestBiz && (
                          <span className="inline-flex items-center gap-1 text-[10px] text-zinc-600 bg-white border border-zinc-200 rounded px-2 py-0.5">
                            <Building2 className="w-3 h-3" /> {v.guestBiz}
                          </span>
                        )}
                        {v.guestPhone && (
                          <a href={`tel:${v.guestPhone}`} className="inline-flex items-center gap-1 text-[10px] text-blue-600 bg-blue-50 border border-blue-100 rounded px-2 py-0.5">
                            <Phone className="w-3 h-3" /> {v.guestPhone}
                          </a>
                        )}
                        {v.guestEmail && (
                          <a href={`mailto:${v.guestEmail}`} className="inline-flex items-center gap-1 text-[10px] text-blue-600 bg-blue-50 border border-blue-100 rounded px-2 py-0.5">
                            <Mail className="w-3 h-3" /> {v.guestEmail}
                          </a>
                        )}
                      </div>
                    )}

                    {/* Status toggle */}
                    <div className="mt-2.5">
                      <button
                        onClick={() => toggleStatus(v)}
                        className={`w-full py-2 text-xs font-bold rounded-lg transition-colors ${
                          v.visitStatus === "visited"
                            ? "bg-green-100 text-green-700 border border-green-200 hover:bg-green-200"
                            : v.visitStatus === "scheduled"
                            ? "bg-amber-100 text-amber-700 border border-amber-200 hover:bg-amber-200"
                            : "bg-zinc-100 text-zinc-600 border border-zinc-200 hover:bg-zinc-200"
                        }`}
                      >
                        {v.visitStatus === "visited" ? (
                          <span className="inline-flex items-center gap-1.5"><CalendarCheck className="w-3.5 h-3.5" /> Visited — tap to change to scheduled</span>
                        ) : v.visitStatus === "scheduled" ? (
                          <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> Scheduled — tap to mark as visited</span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> Set status — tap to mark as visited</span>
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
